import * as React from 'react'
import { useState, useCallback } from 'react'
import * as moment from 'moment'

import { useAdmin } from '../../providers/AdminProvider'

import DropdownList from '../../components/DropdownList'
import TagsInput from '../../components/TagsInput'
import RadarEditor from '../../components/RadarEditor'
import Icon from '../../components/Icon'
import Button from '../../components/Button'
import FormItem from '../../components/FormItem'
import { RadarType, Modification } from '../../api/radar'
import { withCard } from '../../components'

const statuses = [
  { id: 'NEW', title: 'Új' },
  { id: 'IN_PROGRESS', title: 'Folyamatban' },
  { id: 'PROBLEMS', title: 'Problémás' },
  { id: 'DONE', title: 'Készen van' },
  { id: 'APPROVED', title: 'Jóváhagyva' },
  { id: 'SENT', title: 'Kiküldve' },
]

const CollapseButton = ({ open = false, onClick }) => {
  return (
    <span className="cursor-pointer" onClick={onClick}>
      {open ? <Icon name="expand_less" /> : <Icon name="expand_more" />}
    </span>
  )
}

const Tag = ({ value, color = 'amber-600' }) => {
  return (
    <span
      className={`rounded px-2 py-1 mr-1 leading-loose whitespace-nowrap text-white bg-${color}`}
    >
      {value}
    </span>
  )
}

const RadarItem = ({
  value,
  open: initOpen = true,
  admins,
  onChange = (id, value, name) => {},
  onSave = (id) => {},
}: {
  value: RadarType
  open: boolean
  admins: any[]
  onChange: (id: number, value: string | string[] | number, name: string) => void
  onSave: (id: number) => void
}) => {
  const { isAdmin } = useAdmin()
  const [open, setOpen] = useState<boolean>(initOpen)

  const {
    id,
    issue_year,
    issue_number,
    issue_date,
    title,
    text,
    njt_url,
    kozlony_url,
    digest,
    importance,
    digest_status,
    digest_tags,
    assigned_to,
    isModified,
    lists,
    enter_into_force,
  } = value

  const handleChange = useCallback(
    (value: string | string[], name: string) => {
      console.log(`handleChange: ${name} = ${value}`)
      if (name === 'assigned_to') return onChange(id, Number.parseInt(value as string), name)
      onChange(id, value, name)
    },
    [id],
  )

  const handleSave = () => {
    onSave(id)
  }

  const convertDate = (value: string) => {
    console.log('converter')
    const cap =
      /(?<year>[0-9]{4})[.-\s]{0,4}(?<month>[0-9]{1,2})[.-\s]{0,4}(?<day>[0-9]{1,2})[.-\s]?/.exec(
        value.trim(),
      )
    if (cap) {
      const date = moment(value)
      if (date.isValid()) {
        return date.format('YYYY-MM-DD')
      }
    }
    return null
  }

  return (
    <>
      {!open ? (
        <>
          <span className="text-xs">{id}</span>
          <div className="flex flex-row items-start">
            <h5 className="font-bold text-gray-900 dark:text-white">
              {text} {title}
            </h5>
          </div>

          <span className="text-sm">
            Közlöny:{' '}
            <a href={kozlony_url} target="_blank">
              {issue_year}/{issue_number}. ({issue_date})
            </a>
          </span>
          <span className="text-sm">
            Érintett ügyfelek:{' '}
            {lists.map((item) => (
              <>
                <Tag
                  value={item.account_name}
                  color={item.plan === 'PLUS' ? 'amber-600' : 'slate-300'}
                />{' '}
              </>
            ))}
          </span>
          <div className="text-sm">
            Státusz:{' '}
            <Tag
              value={statuses.find((item) => item.id === digest_status)?.title}
              color="sky-400"
            />
          </div>
          <span className="text-sm">
            Címkék:{' '}
            {digest_tags.map((tag) => (
              <Tag value={tag} color="lime-400" />
            ))}
          </span>
          {isAdmin() && (
            <span className="text-sm">
              Delegálva: {admins.find((admin) => admin.id === assigned_to)?.title}
            </span>
          )}

          <CollapseButton open={open} onClick={() => setOpen((prev) => !prev)} />
        </>
      ) : (
        <>
          <span className="text-xs">{id}</span>
          <div className="flex flex-row items-start">
            <h5 className="font-bold text-gray-900 dark:text-white">
              <a href={njt_url} target="_blank">
                {text} {title}
              </a>
            </h5>
          </div>
          <span className="text-sm">
            Közlöny:{' '}
            <a href={kozlony_url} target="_blank">
              {issue_year}/{issue_number}. ({issue_date})
            </a>
          </span>
          <span className="text-sm">
            Érintett ügyfelek:{' '}
            {lists.map((item) => (
              <>
                <Tag
                  value={item.account_name}
                  color={item.plan === 'PLUS' ? 'amber-600' : 'slate-300'}
                />{' '}
              </>
            ))}
          </span>

          <FormItem label="Státusz:">
            <DropdownList
              name="digest_status"
              value={digest_status}
              list={statuses}
              onChange={handleChange}
            />
          </FormItem>
          <FormItem label="Címkék:">
            <TagsInput name="digest_tags" limit={10} value={digest_tags} onChange={handleChange} />
          </FormItem>
          <FormItem label="Fontosság:">
            <DropdownList
              name="importance"
              value={importance}
              list={[
                { id: 'FONTOS_LEHET', title: 'Fontos lehet' },
                { id: 'FONTOS', title: 'Fontos' },
                { id: 'NEM_FONTOS', title: 'Nem fontos' },
              ]}
              onChange={handleChange}
            />
          </FormItem>

          <RadarEditor name="digest" value={digest} onChange={handleChange} />

          <FormItem label="Hatályos / hatálytalan:">
            <TagsInput
              name="enter_into_force"
              limit={10}
              value={enter_into_force}
              placeholder="pl.: 2023-05-12"
              onChange={handleChange}
              converter={convertDate}
            />
          </FormItem>

          {isAdmin() && (
            <FormItem label="Delegálva">
              <DropdownList
                name="assigned_to"
                value={assigned_to}
                list={admins}
                onChange={handleChange}
              />
            </FormItem>
          )}

          <div className="flex flex-row items-center">
            <CollapseButton open={open} onClick={() => setOpen((prev) => !prev)} />
            {isModified === Modification.Modified ? (
              <Button title="Ment" onClick={handleSave} />
            ) : isModified === Modification.Saved ? (
              'Mentve'
            ) : null}
          </div>
        </>
      )}
    </>
  )
}

export default React.memo(withCard(RadarItem))
